/**
 * Tenant context store for the Email Marketing standalone SaaS app.
 *
 * Fetches org info from the backend on app load and exposes it
 * via a Zustand store for use across the app.
 *
 * @module lib/tenant
 */

import { createAppStore } from "@plerity/dashboard-shared/stores";
import { client } from "../api/client";

export interface TenantState {
  org_id: number | null;
  org_name: string | null;
  hostname: string | null;
  plan_tier: string;
  features: string[];
  newsletter_signup_url: string | null;
  loading: boolean;
  error: string | null;
}

interface TenantActions {
  fetchTenantContext: () => Promise<void>;
  setNewsletterSignupUrl: (url: string | null) => void;
  reset: () => void;
}

const initialState: TenantState = {
  org_id: null,
  org_name: null,
  hostname: null,
  plan_tier: "email_basic",
  features: [],
  newsletter_signup_url: null,
  loading: false,
  error: null,
};

export const useTenantStore = createAppStore<TenantState & TenantActions>("email-tenant", (set) => ({
  ...initialState,

  fetchTenantContext: async () => {
    set({ loading: true, error: null });
    try {
      // Wave 2 fix (2026-05-23): the actual backend route is /api/tenant-context.
      // The previous /tenant-context call resolved to the SPA origin instead of
      // the API, hitting the catch-all and 404-ing on every page load.
      const response = await client.get<{
        success: boolean;
        data: {
          org_id: number;
          org_name: string;
          hostname: string | null;
          plan_tier: string;
          features: string[];
          newsletter_signup_url?: string | null;
        };
      }>("/api/tenant-context");

      const { data } = response.data;
      set({
        org_id: data.org_id,
        org_name: data.org_name,
        hostname: data.hostname,
        plan_tier: data.plan_tier,
        features: data.features,
        newsletter_signup_url: data.newsletter_signup_url ?? null,
        loading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load tenant context";
      set({ error: message, loading: false });
    }
  },

  setNewsletterSignupUrl: (url: string | null) => set({ newsletter_signup_url: url }),

  reset: () => set(initialState),
}));

/**
 * Fetch tenant context on app load.
 * Call this once from the root component or auth callback.
 */
export async function fetchTenantContext(): Promise<void> {
  return useTenantStore.getState().fetchTenantContext();
}

/**
 * Switch the active org for super-admin sessions.
 *
 * Updates the admin store's selectedOrgId (which causes all subsequent API
 * requests to include X-Tenant-Id), then re-fetches tenant context so the
 * displayed org name updates immediately. TanStack Query cache invalidation
 * is handled by the OrgSwitcher component via useQueryClient().
 *
 * @param orgId - Target org_id, or null to revert to hostname resolution
 */
export async function switchOrg(orgId: number | null): Promise<void> {
  // Import lazily to avoid a circular dependency at module init time
  const { useAdminStore } = await import("./admin");
  useAdminStore.getState().setSelectedOrgId(orgId);
  await useTenantStore.getState().fetchTenantContext();
}
